import { useState } from 'react';
import { useUpdateTask, useProjects } from '@/hooks/useTasks.ts';
import { isOverdue } from '@/lib/dates.ts';
import { groupTasks, STATUS_LABELS } from '@/lib/grouping.ts';
import { Checkbox, Select } from './ui.tsx';
import { DateField } from './DateField.tsx';
import { PRIORITY_LABELS } from '@shared/types';
import type {
  TaskWithRelations,
  TaskSort,
  SortableField,
  GroupableField,
  TaskStatus,
  Priority,
} from '@shared/types';

interface Column {
  field: SortableField | null;
  label: string;
  /** Fixed widths keep numerals in a column; the task name takes the rest. */
  width: string;
  align?: 'right';
}

const COLUMNS: Column[] = [
  { field: 'title', label: 'Task', width: 'w-auto' },
  { field: 'status', label: 'Status', width: 'w-32' },
  { field: 'priority', label: 'Priority', width: 'w-28' },
  { field: 'due_at', label: 'Due', width: 'w-36' },
  { field: null, label: 'Project', width: 'w-36' },
  { field: null, label: 'Tags', width: 'w-36' },
];

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));
const PRIORITY_OPTIONS = Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label }));

interface Props {
  tasks: TaskWithRelations[];
  sorts: TaskSort[];
  onSortChange: (sorts: TaskSort[]) => void;
  groupBy: GroupableField | null;
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  onOpen: (id: string) => void;
}

export function TaskTable({
  tasks,
  sorts,
  onSortChange,
  groupBy,
  selected,
  onSelectedChange,
  onOpen,
}: Props) {
  const { data: projects } = useProjects();
  const projectNames = new Map((projects ?? []).map((p) => [p.id, p.name]));

  function toggleSort(field: SortableField) {
    const existing = sorts.find((s) => s.field === field);
    if (!existing) return onSortChange([{ field, dir: 'asc' }]);
    if (existing.dir === 'asc') return onSortChange([{ field, dir: 'desc' }]);
    return onSortChange([]);
  }

  function toggleRow(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange(next);
  }

  const allSelected = tasks.length > 0 && tasks.every((t) => selected.has(t.id));
  const groups = groupTasks(tasks, groupBy, projectNames);

  if (tasks.length === 0) {
    return (
      <p className="t-meta rounded-md border border-dashed border-line px-5 py-8">
        No tasks match these filters. Widen them, or clear one.
      </p>
    );
  }

  return (
    // The first column is pinned so the task name stays readable while the
    // rest scrolls on a narrow screen. The wrapper scrolls, never the page.
    <div className="-mx-5 overflow-x-auto sm:mx-0">
      <table className="w-full min-w-[820px] border-collapse">
        <thead>
          <tr className="border-b border-line-strong">
            <th scope="col" className="w-9 py-2 pl-5 sm:pl-2">
              <Checkbox
                checked={allSelected}
                onChange={() => onSelectedChange(allSelected ? new Set() : new Set(tasks.map((t) => t.id)))}
                label={allSelected ? 'Deselect all' : 'Select all'}
              />
            </th>
            {COLUMNS.map((col) => {
              const sort = col.field ? sorts.find((s) => s.field === col.field) : undefined;
              return (
                <th
                  key={col.label}
                  scope="col"
                  className={`${col.width} px-2 py-2 text-left`}
                  aria-sort={sort ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {col.field ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col.field as SortableField)}
                      className="t-label flex items-center gap-1 transition-colors duration-[120ms] ease-out hover:text-ink"
                    >
                      {col.label}
                      <span
                        aria-hidden="true"
                        className={`text-[9px] transition-opacity duration-[120ms] ${sort ? 'text-brass opacity-100' : 'opacity-0'}`}
                      >
                        {sort?.dir === 'desc' ? '▼' : '▲'}
                      </span>
                    </button>
                  ) : (
                    <span className="t-label">{col.label}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {groups.map((group) => (
            <GroupRows
              key={group.key}
              label={group.label}
              tasks={group.tasks}
              showHeader={groupBy !== null}
              selected={selected}
              onToggleRow={toggleRow}
              onOpen={onOpen}
              projectNames={projectNames}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({
  label,
  tasks,
  showHeader,
  selected,
  onToggleRow,
  onOpen,
  projectNames,
}: {
  label: string;
  tasks: TaskWithRelations[];
  showHeader: boolean;
  selected: Set<string>;
  onToggleRow: (id: string) => void;
  onOpen: (id: string) => void;
  projectNames: Map<string, string>;
}) {
  return (
    <>
      {showHeader && (
        <tr>
          <td colSpan={COLUMNS.length + 1} className="pt-6 pb-1 pl-5 sm:pl-2">
            <span className="t-label">{label}</span>
            <span className="t-meta ml-2 text-ink-3" data-numeric>
              {tasks.length}
            </span>
          </td>
        </tr>
      )}
      {tasks.map((task) => (
        <TaskTableRow
          key={task.id}
          task={task}
          selected={selected.has(task.id)}
          onToggle={() => onToggleRow(task.id)}
          onOpen={() => onOpen(task.id)}
          projectName={task.project_id ? (projectNames.get(task.project_id) ?? null) : null}
        />
      ))}
    </>
  );
}

const RAIL_HEIGHT: Record<number, string> = { 0: '0px', 1: '8px', 2: '14px', 3: '20px' };

function TaskTableRow({
  task,
  selected,
  onToggle,
  onOpen,
  projectName,
}: {
  task: TaskWithRelations;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  projectName: string | null;
}) {
  const updateTask = useUpdateTask();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);

  const closed = task.status === 'done' || task.status === 'cancelled';
  const overdue = !closed && isOverdue(task.due_at);

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== task.title) updateTask.mutate({ id: task.id, title: trimmed });
    else setDraft(task.title);
  }

  return (
    <tr
      className={`group border-b border-line transition-colors duration-[120ms] ease-out ${
        selected ? 'bg-hover' : 'hover:bg-hover/50'
      }`}
    >
      <td className="relative py-1.5 pl-5 align-middle sm:pl-2">
        {/* Same ruled margin as the lists, so priority reads identically in
            both places instead of being a dot here and a word there. */}
        <span
          aria-hidden="true"
          className="rail"
          style={{
            height: closed ? '0px' : RAIL_HEIGHT[task.priority],
            background: overdue ? 'var(--color-brick)' : 'var(--color-brass)',
          }}
        />
        <Checkbox checked={selected} onChange={onToggle} label={`Select ${task.title}`} />
      </td>

      <td className="px-2 py-1.5">
        {editing ? (
          <input
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setDraft(task.title);
                setEditing(false);
              }
            }}
            className="w-full rounded-xs border border-brass bg-page px-1.5 py-0.5 text-sm text-ink focus:outline-none"
          />
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className={`min-w-0 flex-1 truncate text-left text-sm ${closed ? 'text-ink-3 line-through' : 'text-ink'}`}
            >
              {task.title}
            </button>
            {task.subtask_count > 0 && (
              <span className="t-meta shrink-0 text-ink-3" data-numeric>
                {task.subtask_done_count}/{task.subtask_count}
              </span>
            )}
            <button
              type="button"
              onClick={onOpen}
              className="t-meta shrink-0 text-ink-3 opacity-0 transition-opacity duration-[120ms] ease-out group-hover:opacity-100 focus-visible:opacity-100"
            >
              Open
            </button>
          </div>
        )}
      </td>

      {/* The static "Open" text that used to sit beside this dropdown is gone —
          it restated the same field twice in every row. */}
      <td className="px-2 py-1.5">
        <Select
          value={task.status}
          onChange={(v) => updateTask.mutate({ id: task.id, status: v as TaskStatus })}
          options={STATUS_OPTIONS}
          ariaLabel={`Status of ${task.title}`}
        />
      </td>

      <td className="px-2 py-1.5">
        <Select
          value={String(task.priority)}
          onChange={(v) => updateTask.mutate({ id: task.id, priority: Number(v) as Priority })}
          options={PRIORITY_OPTIONS}
          ariaLabel={`Priority of ${task.title}`}
        />
      </td>

      <td className={`px-2 py-1.5 ${overdue ? 'text-brick' : ''}`}>
        <DateField
          value={task.due_at}
          onChange={(iso) => updateTask.mutate({ id: task.id, due_at: iso })}
          ariaLabel={`Due date of ${task.title}`}
          settled={closed}
        />
      </td>

      <td className="t-meta truncate px-2 py-1.5 text-ink-3">{projectName ?? '—'}</td>

      <td className="t-meta truncate px-2 py-1.5 text-ink-3">
        {task.tags.length ? task.tags.map((t) => `#${t.name}`).join(' ') : '—'}
      </td>
    </tr>
  );
}
